import React from "react";
import moment from "moment";
import submit from "submit-form";

const {
  getTargetData,
  getNutritionPlan,
} = require("../../../../utils/nutrition-helpers");
import { withTranslation } from "react-i18next";
import { getDisplayDate } from "../../../../utils/date-helpers";

import NutritionTarget from "./NutritionTarget";
import NutrientsChart from "./NutrientsChart";
import BodyweightSummary from "./BodyweightSummary";
import GraphSummary from "./GraphSummary";
import FoodLog from "./FoodLog";
import MealPhotosDisplay from "./MealPhotosDisplay";

class NutritionSummary extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      dataView: "nutrition",
    };

    this.handleDataViewChange = this.handleDataViewChange.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.dataView !== "nutrition" &&
      prevProps.activeDayUtc !== this.props.activeDayUtc
    ) {
      this.handleDataViewChange("nutrition");
    }
  }

  handleDataViewChange(dataView) {
    const { query = {} } = this.props;
    if (dataView === "mealPhotos")
      submit(`/actions/refresh`, {
        method: "GET",
        body: {
          ...query,
          utcDate: this.props.activeDayUtc,
          dataView,
        },
      });
    setTimeout(() => {
      this.setState({ dataView });
    }, 100);
  }

  render() {
    const {
      activeDayUtc,
      dayStats,
      dateFrom,
      dateTo,
      stats,
      withToggle = false,
      displayDate = true,
      showNutritionAlways = false,
      nutrientsChartHeight = 275,
      nutritionPlans = [],
      isCalendar = false,
      activeExercises,
      query = {},
      mealPhotos,
      t,
    } = this.props;
    const { dataView } = this.state;

    const isPeriod = dayStats ? dayStats.isPeriod || false : false;

    const startOfWeek = isPeriod ? dayStats.startOfWeek : null;
    const endOfWeek = isPeriod ? dayStats.endOfWeek : null;

    const nutritionPlan = getNutritionPlan(
      nutritionPlans,
      activeDayUtc,
      isPeriod,
      startOfWeek,
      endOfWeek
    );

    const targetData = getTargetData({
      nutritionPlan,
      isPeriod,
      startOfWeek,
      endOfWeek,
      averageValues: true,
      activeExercises,
    });

    if (dataView !== "mealPhotos" && !dayStats)
      return (
        <div className="nutrition-summary">
          <div className="nutrition-summary-head empty-state h-full">
            <span className="font-bold">
              {t("nutritionPage.summary.noDataToShow")}
            </span>
          </div>
        </div>
      );

    const showBodyweight = dataView === "bodyweight";
    const showNutrition = dataView === "nutrition";
    const showSteps = dataView === "steps";
    const showSleep = dataView === "sleep";
    const showFoodLog = dataView === "foodlog";
    const showMealPhotos = dataView === "mealPhotos";

    const showTargetData = isPeriod ? nutritionPlan.length : !!nutritionPlan;

    const showNutritionTarget =
      (showNutrition && showTargetData) || showNutritionAlways;

    const showNutrientsChart =
      showNutrition && !!(dayStats && dayStats.nutrients);

    // Failsafe in case there are no nutrition plans and there is no nutrition in the period
    // This is because the periods always have data in the nutrients
    const showNoDataMessage = isPeriod
      ? Object.values(dayStats.nutrients).every((val) => val === 0) &&
        nutritionPlan.length === 0
      : !showNutrientsChart && !showNutritionTarget;

    return (
      <div className="nutrition-summary">
        <div className="nutrition-summary-head">
          {withToggle && (
            <header className="summary-toggles">
              <button
                data-button-type={
                  dataView === "nutrition" ? "primary" : "secondary"
                }
                onClick={() => this.handleDataViewChange("nutrition")}
              >
                {t("nutritionPage.summary.summary")}
              </button>
              <button
                data-button-type={
                  dataView === "foodlog" ? "primary" : "secondary"
                }
                onClick={() => this.handleDataViewChange("foodlog")}
              >
                {t("nutritionPage.summary.foodlog")}
              </button>
              <button
                data-button-type={
                  dataView === "mealPhotos" ? "primary" : "secondary"
                }
                onClick={() => this.handleDataViewChange("mealPhotos")}
              >
                {t("nutritionPage.summary.mealPhotos")}
              </button>
              {!isCalendar && (
                <>
                  <button
                    data-button-type={
                      dataView === "bodyweight" ? "primary" : "secondary"
                    }
                    onClick={() => this.handleDataViewChange("bodyweight")}
                  >
                    {t("nutritionPage.summary.bodyweight")}
                  </button>
                  <button
                    data-button-type={
                      dataView === "steps" ? "primary" : "secondary"
                    }
                    onClick={() => this.handleDataViewChange("steps")}
                  >
                    {t("nutritionPage.summary.steps")}
                  </button>
                </>
              )}
              {/* Hidden as there is no sleep implemented yet */}
            </header>
          )}
          {displayDate && !showFoodLog && !showMealPhotos ? (
            showBodyweight || showSteps || showSleep ? (
              <div className="summary-title">
                <span>
                  {getDisplayDate(dateFrom)} to {getDisplayDate(dateTo)}
                </span>
              </div>
            ) : isPeriod ? (
              <div className="summary-title">
                <span>
                  {getDisplayDate(dayStats.startOfWeek, false)} to{" "}
                  {getDisplayDate(moment(dayStats.endOfWeek), false)}
                </span>
              </div>
            ) : (
              <div className="summary-title">
                <span>{getDisplayDate(activeDayUtc, true)}</span>{" "}
              </div>
            )
          ) : (
            <div></div>
          )}
        </div>
        {showBodyweight && (
          <BodyweightSummary
            dateFrom={dateFrom}
            dateTo={dateTo}
            stats={stats}
          />
        )}
        {showNutritionTarget && !showFoodLog && (
          <NutritionTarget
            targetData={targetData}
            dayStats={dayStats}
            showFiberAlways={showNutritionAlways}
          />
        )}
        {showSteps && (
          <GraphSummary
            dateFrom={dateFrom}
            dateTo={dateTo}
            stats={stats}
            field={"steps"}
          />
        )}
        {showSleep && (
          <GraphSummary
            dateFrom={dateFrom}
            dateTo={dateTo}
            stats={stats}
            field={"sleep"}
            units={"hrs"}
          />
        )}
        {showNutrientsChart && !showNoDataMessage && (
          <NutrientsChart
            targetData={targetData}
            dayStats={dayStats}
            height={nutrientsChartHeight}
          />
        )}
        {showFoodLog && (
          <FoodLog
            stats={dayStats}
            isPeriod={isPeriod}
            isCalendar={isCalendar}
          />
        )}
        {showMealPhotos && (
          <MealPhotosDisplay
            activeDayUtc={activeDayUtc}
            dayStats={dayStats}
            mealPhotos={mealPhotos}
            locals={this.props.locals}
          />
        )}
        {showNoDataMessage &&
          !showBodyweight &&
          !showSleep &&
          !showSteps &&
          !showFoodLog &&
          !showMealPhotos && (
            <div className="empty-state h-full">
              <span className="font-bold">
                {t("nutritionPage.summary.noNutritionData")}
              </span>
            </div>
          )}
      </div>
    );
  }
}

export default withTranslation("athlete-portal")(NutritionSummary);
